import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import countryList from 'react-select-country-list';
import Select from 'react-select';
import './CustomerForm.css';

const branchOptions = [
  { value: 'Rekonic UAE', label: 'Rekonic UAE' },
  { value: 'Rekonic KSA', label: 'Rekonic KSA' }
];

const customerTypeOptions = [
  { value: 'individual', label: 'Individual' },
  { value: 'b2b', label: 'B2B Business' }
];

const emptyForm = {
  branch: '',
  customer_type: '',
  relationship_staff: '',
  full_name: '',
  phone_number: '',
  nationality: '',
  residence_country: '',
  address: '',
  passport_number: '',
  trn_number: '',
  opening_balance: '',
  email: ''
};

const CustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState(emptyForm);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [status, setStatus] = useState({ success: '', error: null });
  const [loadingData, setLoadingData] = useState(isEditMode);

  const countryOptions = Array.isArray(countryList().getData ? countryList().getData() : countryList())
    ? countryList().getData()
    : [];

  // Fetch employees for the dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees/');
        const options = response.data.map(emp => ({
          value: `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name || ''}`.replace(/\s+/g, ' ').trim(),
          label: `${emp.first_name} ${emp.middle_name || ''} ${emp.last_name || ''}`.replace(/\s+/g, ' ').trim()
        }));
        setEmployeeOptions(options);
      } catch (error) {
        console.error('Error fetching employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // If edit mode, pre-fill form with existing customer data
  useEffect(() => {
    if (!isEditMode) return;
    const fetchCustomer = async () => {
      try {
        const response = await api.get(`/customers/${id}`);
        const c = response.data;
        setFormData({
          branch: c.branch_name || '',
          customer_type: c.customer_type || '',
          relationship_staff: c.relationship_staff || '',
          full_name: c.full_name || '',
          phone_number: c.phone_number ? c.phone_number.replace(/^\+/, '') : '',
          nationality: c.nationality || '',
          residence_country: c.residence_country || '',
          address: c.full_address || '',
          passport_number: c.passport_number || '',
          trn_number: c.trn_number || '',
          opening_balance: c.opening_balance !== undefined ? String(c.opening_balance) : '',
          email: c.email || ''
        });
      } catch (err) {
        console.error('Error loading customer for edit:', err);
        setStatus({ success: '', error: [{ msg: 'Failed to load customer data.' }] });
      } finally {
        setLoadingData(false);
      }
    };
    fetchCustomer();
  }, [id, isEditMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected ? selected.value : ''
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone_number: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      branch, customer_type, relationship_staff, full_name,
      phone_number, nationality, residence_country, address,
      passport_number, trn_number, opening_balance, email
    } = formData;

    if (
      !branch || !customer_type || !relationship_staff || !full_name ||
      !phone_number || !nationality || !residence_country || !address ||
      (customer_type === 'individual' && !passport_number) ||
      (customer_type === 'b2b' && !trn_number)
    ) {
      alert('Please fill in all required fields.');
      return;
    }

    const payload = {
      branch_name: formData.branch,
      customer_type: formData.customer_type,
      relationship_staff: formData.relationship_staff,
      full_name: formData.full_name,
      phone_number: '+' + formData.phone_number,
      nationality: formData.nationality,
      residence_country: formData.residence_country,
      full_address: formData.address,
      opening_balance: parseFloat(formData.opening_balance) || 0,
      email: formData.email || null
    };

    if (formData.customer_type === 'individual') payload.passport_number = formData.passport_number;
    if (formData.customer_type === 'b2b') payload.trn_number = formData.trn_number;

    try {
      if (isEditMode) {
        await api.put(`/customers/${id}`, payload);
        setStatus({ success: 'Customer updated successfully!', error: null });
        setTimeout(() => navigate(`/dashboard/customers/${id}`), 1200);
      } else {
        await api.post('/customers/register', payload);
        setStatus({ success: 'Customer added successfully!', error: null });
        setFormData(emptyForm);
      }
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData && Array.isArray(errorData.detail)) {
        const formattedErrors = errorData.detail.map((e) => ({
          msg: e.msg || `${e.loc?.join('.')} - invalid`
        }));
        setStatus({ success: '', error: formattedErrors });
      } else {
        setStatus({
          success: '',
          error: [{ msg: errorData?.detail || 'Operation failed. Please check your input.' }]
        });
      }
    }
  };

  if (loadingData) return <div className="container mt-4"><p>Loading customer data...</p></div>;

  return (
    <div className="customer-form-container">
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>{isEditMode ? '✏️ Edit Customer' : 'Customer Registration'}</h2>
          <button
            className="btn btn-secondary"
            onClick={() => isEditMode ? navigate(`/dashboard/customers/${id}`) : navigate('/dashboard')}
          >
            {isEditMode ? '← Back to Details' : 'Back to Dashboard'}
          </button>
        </div>
        <form onSubmit={handleSubmit} className="shadow p-4 bg-white rounded">

          <div className="mb-3">
            <label className="form-label">Branch Name *</label>
            <Select
              options={branchOptions}
              value={branchOptions.find((option) => option.value === formData.branch) || null}
              onChange={(selected) => handleSelectChange(selected, 'branch')}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Customer Type *</label>
            <Select
              options={customerTypeOptions}
              value={customerTypeOptions.find((option) => option.value === formData.customer_type) || null}
              onChange={(selected) => handleSelectChange(selected, 'customer_type')}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Relationship Staff *</label>
            <Select
              options={employeeOptions}
              value={employeeOptions.find((option) => option.value === formData.relationship_staff) || null}
              onChange={(selected) => handleSelectChange(selected, 'relationship_staff')}
              placeholder="Select Relationship Staff"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Full Name *</label>
            <input
              type="text"
              className="form-control"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Email Address (Optional)</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Phone Number *</label>
            <PhoneInput
              country={'ae'}
              value={formData.phone_number}
              onChange={handlePhoneChange}
              inputClass="form-control"
              containerClass="phone-input-container"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Nationality *</label>
            <Select
              options={countryOptions}
              value={countryOptions.find((option) => option.value === formData.nationality) || null}
              onChange={(selected) => handleSelectChange(selected, 'nationality')}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Residence Country *</label>
            <Select
              options={countryOptions}
              value={countryOptions.find((option) => option.value === formData.residence_country) || null}
              onChange={(selected) => handleSelectChange(selected, 'residence_country')}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Address *</label>
            <textarea
              className="form-control"
              name="address"
              value={formData.address}
              onChange={handleChange}
            ></textarea>
          </div>

          {formData.customer_type === 'individual' && (
            <div className="mb-3">
              <label className="form-label">Passport Number *</label>
              <input
                type="text"
                className="form-control"
                name="passport_number"
                value={formData.passport_number}
                onChange={handleChange}
              />
            </div>
          )}

          {formData.customer_type === 'b2b' && (
            <div className="mb-3">
              <label className="form-label">TRN Number *</label>
              <input
                type="text"
                className="form-control"
                name="trn_number"
                value={formData.trn_number}
                onChange={handleChange}
              />
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">Opening Balance</label>
            <input
              type="number"
              className="form-control"
              name="opening_balance"
              value={formData.opening_balance}
              onChange={handleChange}
            />
          </div>

          <button type="submit" className="btn btn-primary w-100">
            {isEditMode ? 'Update Customer' : 'Submit'}
          </button>

          {status.success && <div className="alert alert-success mt-3">{status.success}</div>}

          {status.error && Array.isArray(status.error) && (
            <div className="alert alert-danger mt-3">
              <ul className="mb-0">
                {status.error.map((err, index) => (
                  <li key={index}>{err.msg}</li>
                ))}
              </ul>
            </div>
          )}

        </form>
      </div>
    </div>
  );
};

export default CustomerForm;
